/**
 * Athlete App - Enhanced Settings Page
 *
 * Comprehensive profile management including:
 * - Basic profile info
 * - Training profile (for AI personalization)
 * - Goals & aspirations
 * - Health & wellness
 * - Notifications
 * - Privacy
 */

import { useState, useEffect, useMemo } from 'react';
import { useUser, updateProfile, exportAthleteData, listFamilyLinks, updateNotificationPreferences, deleteFamilyLink, listOrgs, getMySubscription, createPortalSession, createCheckoutSession, getPricing, listPaymentPlans, getGuardianSubscription, guardianSubscribe, cancelGuardianSubscription, athleteSubscribe, getAthleteDirectSubscription, cancelAthleteDirectSubscription, type FamilyLink, type Organization, type UserSubscription, type SubscriptionTier, type SubscriptionStatus, type PricingResponse, type OrgPaymentPlan, type GuardianSubscription as GuardianSubType, type AthleteSubscription as AthleteCoachSubType } from '@tss/shared';
import {
  User,
  Bell,
  Shield,
  Download,
  Eye,
  Loader2,
  Check,
  Target,
  Dumbbell,
  Brain,
  Clock,
  MapPin,
  GraduationCap,
  Plus,
  X,
  Sparkles,
  ChevronRight,
  Users,
  CheckCircle,
  BellOff,
  Trash2,
  AlertCircle,
  CreditCard,
  Coins,
  ExternalLink,
  RefreshCw,
  Building2,
} from 'lucide-react';
import { OnboardingChat } from '../components/OnboardingChat';

type SettingsTab = 'profile' | 'training' | 'goals' | 'notifications' | 'privacy' | 'family' | 'billing' | 'organization';

/**
 * Calculate age from date of birth string
 */
function calculateAge(dateOfBirth: string | null | undefined): number | null {
  if (!dateOfBirth) return null;

  const dob = new Date(dateOfBirth);
  if (isNaN(dob.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }

  return age;
}

type AgeGroup = 'under13' | '13to17' | 'adult' | 'unknown';

function getAgeGroup(age: number | null): AgeGroup {
  if (age === null) return 'unknown';
  if (age < 13) return 'under13';
  if (age < 18) return '13to17';
  return 'adult';
}

interface ProfileForm {
  // Basic profile
  given_name: string;
  family_name: string;
  position: string;
  jersey_number: string;
  height: string;
  weight: string;
  date_of_birth: string;
  dominant_hand: string;
  // Learning & motivation
  learning_style: string;
  motivation_type: string;
  preferred_feedback_style: string;
  // Training preferences
  preferred_workout_time: string;
  preferred_workout_duration: string;
  equipment_available: string[];
  training_environment: string;
  // Goals
  athletic_goals: string[];
  target_level: string;
  graduation_year: string;
  // Notifications
  notify_on_comment: boolean;
  notify_on_feedback: boolean;
  notify_on_assignment: boolean;
}

const EQUIPMENT_OPTIONS = [
  'Basketball',
  'Hoop/Net',
  'Cones',
  'Agility Ladder',
  'Resistance Bands',
  'Weights/Dumbbells',
  'Medicine Ball',
  'Jump Rope',
  'Foam Roller',
];

const POSITION_OPTIONS = [
  'Point Guard',
  'Shooting Guard',
  'Small Forward',
  'Power Forward',
  'Center',
  'Guard',
  'Forward',
  'Wing',
];

// Height helpers - parse "6'2\"" to {feet, inches} and back
function parseHeight(height: string): { feet: string; inches: string } {
  if (!height) return { feet: '', inches: '' };
  const match = height.match(/(\d+)'(\d+)/);
  if (match) return { feet: match[1], inches: match[2] };
  // Try just feet
  const feetMatch = height.match(/(\d+)'/);
  if (feetMatch) return { feet: feetMatch[1], inches: '0' };
  return { feet: '', inches: '' };
}

function formatHeight(feet: string, inches: string): string {
  if (!feet) return '';
  return `${feet}'${inches || '0'}"`;
}

// Weight helper - parse "185 lbs" to number and back
function parseWeight(weight: string): string {
  if (!weight) return '';
  const match = weight.match(/(\d+)/);
  return match ? match[1] : '';
}

function formatWeight(lbs: string): string {
  if (!lbs) return '';
  return `${lbs} lbs`;
}

interface SettingsPageProps {
  isGuardian?: boolean;
  organizations?: Organization[];
  currentOrg?: Organization | null;
  onSelectOrg?: (org: Organization) => void;
}

export function SettingsPage({ isGuardian = false, organizations, currentOrg: propCurrentOrg, onSelectOrg }: SettingsPageProps) {
  const { userProfile, displayName, loadUserProfile } = useUser();
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [newGoal, setNewGoal] = useState('');
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Family state
  const [familyLinks, setFamilyLinks] = useState<FamilyLink[]>([]);
  const [familyLoading, setFamilyLoading] = useState(false);
  const [localCurrentOrg, setLocalCurrentOrg] = useState<Organization | null>(null);
  const [localAllOrgs, setLocalAllOrgs] = useState<Organization[]>([]);

  // Use props if provided, otherwise use local state
  const currentOrg = propCurrentOrg ?? localCurrentOrg;
  const allOrgs = organizations ?? localAllOrgs;
  const setCurrentOrg = onSelectOrg ?? setLocalCurrentOrg;

  // Filter orgs for billing - only show orgs where user is ATHLETE or GUARDIAN
  // Prevents showing orgs where user has elevated roles (COACH, OWNER, MODERATOR)
  const billingOrgs = useMemo(() => {
    const elevatedRoles = ['COACH', 'OWNER', 'MODERATOR'];
    return allOrgs.filter(org => !elevatedRoles.includes(org.role.toUpperCase()));
  }, [allOrgs]);

  // Subscription state
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState<SubscriptionTier | null>(null);
  const [pricing, setPricing] = useState<PricingResponse | null>(null);

  // Billing checkout state
  const [guardianCheckoutLoading, setGuardianCheckoutLoading] = useState<string | null>(null);
  const [cancellingGuardianSub, setCancellingGuardianSub] = useState(false);
  const [athleteCheckoutLoading, setAthleteCheckoutLoading] = useState<string | null>(null);
  const [cancellingAthleteSub, setCancellingAthleteSub] = useState(false);

  // Multi-org billing data (for parents with kids in different orgs)
  type OrgBillingData = {
    org: Organization;
    subscription: AthleteCoachSubType | GuardianSubType | null;
    plans: OrgPaymentPlan[];
    familyLinks: FamilyLink[];
  };
  const [allOrgsBillingData, setAllOrgsBillingData] = useState<OrgBillingData[]>([]);
  const [allOrgsBillingLoading, setAllOrgsBillingLoading] = useState(false);

  // Age-based permissions
  const age = useMemo(() => calculateAge(userProfile?.date_of_birth), [userProfile?.date_of_birth]);
  const ageGroup = useMemo(() => getAgeGroup(age), [age]);
  const canManageNotifications = ageGroup === '13to17' || ageGroup === 'adult' || ageGroup === 'unknown';
  const canRemoveLinks = ageGroup === 'adult';

  const [form, setForm] = useState<ProfileForm>({
    given_name: '',
    family_name: '',
    position: '',
    jersey_number: '',
    height: '',
    weight: '',
    date_of_birth: '',
    dominant_hand: '',
    learning_style: '',
    motivation_type: '',
    preferred_feedback_style: '',
    preferred_workout_time: '',
    preferred_workout_duration: '',
    equipment_available: [],
    training_environment: '',
    athletic_goals: [],
    target_level: '',
    graduation_year: '',
    notify_on_comment: true,
    notify_on_feedback: true,
    notify_on_assignment: true,
  });

  // Load organization on mount
  useEffect(() => {
    // Only load orgs if not provided via props
    if (organizations) return;

    async function loadOrg() {
      try {
        const { orgs } = await listOrgs();
        setLocalAllOrgs(orgs);
        if (orgs.length > 0) {
          setLocalCurrentOrg(orgs[0]);
        }
      } catch (err) {
        console.error('Failed to load organization:', err);
      }
    }
    loadOrg();
  }, [organizations]);

  // Load family links when family tab is active
  useEffect(() => {
    async function loadFamilyLinks() {
      if (activeTab !== 'family' || !currentOrg) return;
      setFamilyLoading(true);
      try {
        const { links } = await listFamilyLinks(currentOrg.org_id);
        setFamilyLinks(links || []);
      } catch (err) {
        console.error('Failed to load family links:', err);
      } finally {
        setFamilyLoading(false);
      }
    }
    loadFamilyLinks();
  }, [activeTab, currentOrg]);

  // Load subscription when billing tab is active
  const loadSubscription = async () => {
    setSubscriptionLoading(true);
    setSubscriptionError(null);
    try {
      const sub = await getMySubscription();
      setSubscription(sub);
    } catch (err) {
      if (err instanceof Error && err.message.includes('404')) {
        setSubscription(null);
      } else {
        setSubscriptionError(err instanceof Error ? err.message : 'Failed to load subscription');
      }
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Load pricing from backend
  const loadPricing = async () => {
    try {
      const pricingData = await getPricing();
      setPricing(pricingData);
    } catch (err) {
      console.error('Failed to load pricing:', err);
    }
  };

  const openBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const { portal_url } = await createPortalSession();
      window.open(portal_url, '_blank');
    } catch (err) {
      setSubscriptionError(err instanceof Error ? err.message : 'Failed to open billing portal');
    } finally {
      setPortalLoading(false);
    }
  };

  const startCheckout = async (tier: SubscriptionTier) => {
    setCheckoutLoading(tier);
    setSubscriptionError(null);
    try {
      const { checkout_url } = await createCheckoutSession({
        tier,
        success_url: `${window.location.origin}/settings?billing=success`,
        cancel_url: `${window.location.origin}/settings?billing=cancelled`,
      });
      window.location.href = checkout_url;
    } catch (err) {
      setSubscriptionError(err instanceof Error ? err.message : 'Failed to start checkout');
      setCheckoutLoading(null);
    }
  };

  // Load billing data for all orgs (for parents with kids in multiple teams)
  // Uses billingOrgs to exclude orgs where user has elevated roles
  const loadAllOrgsBillingData = async () => {
    if (billingOrgs.length === 0) return;

    setAllOrgsBillingLoading(true);
    try {
      const billingDataPromises = billingOrgs.map(async (org) => {
        const [subResult, plansResult, linksResult] = await Promise.all([
          isGuardian
            ? getGuardianSubscription(org.org_id).catch(() => null)
            : getAthleteDirectSubscription(org.org_id).catch(() => null),
          listPaymentPlans(org.org_id).catch(() => ({ plans: [] })),
          isGuardian
            ? listFamilyLinks(org.org_id).catch(() => ({ links: [] }))
            : Promise.resolve({ links: [] }),
        ]);

        // Filter plans based on user type
        const plans = isGuardian
          ? (plansResult.plans || []).filter(p => p.target_audience === 'GUARDIAN_ONLY' || p.target_audience === 'BOTH')
          : (plansResult.plans || []).filter(p => p.target_audience === 'ATHLETE_ONLY' || p.target_audience === 'BOTH');

        return {
          org,
          subscription: subResult,
          plans,
          familyLinks: (linksResult.links || []).filter((l: FamilyLink) => l.status === 'APPROVED'),
        };
      });

      const results = await Promise.all(billingDataPromises);
      setAllOrgsBillingData(results);
    } catch (err) {
      console.error('Failed to load all orgs billing data:', err);
      setSubscriptionError(err instanceof Error ? err.message : 'Failed to load billing data');
    } finally {
      setAllOrgsBillingLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'billing') {
      // Load billing for all orgs at once (handles coach subscriptions for both guardians and athletes)
      loadAllOrgsBillingData();

      if (!isGuardian) {
        // Athletes also see platform subscriptions
        if (!subscription && !subscriptionLoading) {
          loadSubscription();
        }
        if (!pricing) {
          loadPricing();
        }
      }
    }
  }, [activeTab, isGuardian, billingOrgs]);

  // Helper to format tier name
  const formatTierName = (tier: SubscriptionTier): string => {
    const names: Record<SubscriptionTier, string> = {
      'FREE': 'Free',
      'ATHLETE_BASIC': 'Athlete Basic',
      'ATHLETE_PRO': 'Athlete Pro',
      'COACH_BASIC': 'Coach Basic',
      'COACH_PRO': 'Coach Pro',
      'ENTERPRISE': 'Enterprise'
    };
    return names[tier] || tier;
  };

  const getStatusColor = (status: SubscriptionStatus): string => {
    const colors: Record<SubscriptionStatus, string> = {
      'ACTIVE': '#10b981',
      'TRIAL': '#3b82f6',
      'PAST_DUE': '#f59e0b',
      'CANCELLED': '#6b7280',
      'SUSPENDED': '#ef4444'
    };
    return colors[status] || '#6b7280';
  };

  // Initialize form with user profile data
  useEffect(() => {
    if (userProfile) {
      setForm({
        given_name: userProfile.given_name || '',
        family_name: userProfile.family_name || '',
        position: userProfile.position || '',
        jersey_number: userProfile.jersey_number || '',
        height: userProfile.height || '',
        weight: userProfile.weight || '',
        date_of_birth: userProfile.date_of_birth || '',
        dominant_hand: userProfile.dominant_hand || '',
        learning_style: userProfile.learning_style || '',
        motivation_type: userProfile.motivation_type || '',
        preferred_feedback_style: userProfile.preferred_feedback_style || '',
        preferred_workout_time: userProfile.preferred_workout_time || '',
        preferred_workout_duration: userProfile.preferred_workout_duration || '',
        equipment_available: userProfile.equipment_available || [],
        training_environment: userProfile.training_environment || '',
        athletic_goals: userProfile.athletic_goals || [],
        target_level: userProfile.target_level || '',
        graduation_year: userProfile.graduation_year?.toString() || '',
        notify_on_comment: userProfile.notify_on_comment ?? true,
        notify_on_feedback: userProfile.notify_on_feedback ?? true,
        notify_on_assignment: userProfile.notify_on_assignment ?? true,
      });
    }
  }, [userProfile]);

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      // Convert graduation_year to number if present
      const updates: Record<string, any> = { ...form };
      if (form.graduation_year) {
        updates.graduation_year = parseInt(form.graduation_year, 10) || null;
      } else {
        updates.graduation_year = null;
      }

      await updateProfile(updates);
      await loadUserProfile();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  }

  async function handleExportData() {
    setExporting(true);
    try {
      await exportAthleteData();
      alert('Export request submitted. You will receive an email with your data.');
    } catch (err) {
      alert('Failed to request data export. Please try again.');
    } finally {
      setExporting(false);
    }
  }

  function handleStartOnboarding() {
    localStorage.removeItem('onboardingSkipped');
    setShowOnboarding(true);
  }

  function handleOnboardingComplete() {
    setShowOnboarding(false);
    loadUserProfile();
  }

  function handleOnboardingSkip() {
    localStorage.setItem('onboardingSkipped', 'true');
    setShowOnboarding(false);
  }

  function toggleArrayItem(field: keyof ProfileForm, item: string) {
    const current = form[field] as string[];
    if (current.includes(item)) {
      setForm({ ...form, [field]: current.filter(i => i !== item) });
    } else {
      setForm({ ...form, [field]: [...current, item] });
    }
  }

  function addGoal() {
    if (newGoal.trim() && !form.athletic_goals.includes(newGoal.trim())) {
      setForm({ ...form, athletic_goals: [...form.athletic_goals, newGoal.trim()] });
      setNewGoal('');
    }
  }

  function removeGoal(goal: string) {
    setForm({ ...form, athletic_goals: form.athletic_goals.filter(g => g !== goal) });
  }

  // Guardians see simplified tabs (no training/goals/family - they manage athletes in My Athletes page)
  const baseTabs: { id: SettingsTab; label: string; icon: typeof User }[] = isGuardian
    ? [
        { id: 'profile', label: 'Profile', icon: User },
        { id: 'notifications', label: 'Notifications', icon: Bell },
        { id: 'privacy', label: 'Privacy', icon: Shield },
        { id: 'billing', label: 'Billing', icon: CreditCard },
      ]
    : [
        { id: 'profile', label: 'Profile', icon: User },
        { id: 'training', label: 'Training', icon: Dumbbell },
        { id: 'goals', label: 'Goals', icon: Target },
        { id: 'notifications', label: 'Notifications', icon: Bell },
        { id: 'privacy', label: 'Privacy', icon: Shield },
        { id: 'family', label: 'Family', icon: Users },
        { id: 'billing', label: 'Billing', icon: CreditCard },
      ];

  // Add organization tab if user belongs to multiple orgs
  const tabs = allOrgs.length > 1
    ? [...baseTabs, { id: 'organization' as SettingsTab, label: 'Organization', icon: Building2 }]
    : baseTabs;

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto' }}>
      {/* Onboarding chat modal */}
      {showOnboarding && (
        <OnboardingChat
          userName={displayName}
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
        />
      )}

      <h1 style={{ fontSize: '28px', fontWeight: '700', color: 'var(--gray-900)', marginBottom: '24px' }}>
        Settings
      </h1>

      {/* Tab navigation */}
      <div className="tab-group" style={{ marginBottom: '24px', flexWrap: 'wrap' }}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Success/Error messages */}
      {saved && (
        <div
          style={{
            padding: '12px 16px',
            background: 'var(--success-bg-light)',
            border: '1px solid var(--success-border-light)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--success)',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Check size={18} />
          Changes saved successfully
        </div>
      )}
      {error && (
        <div
          style={{
            padding: '12px 16px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--error)',
            marginBottom: '16px',
          }}
        >
          {error}
        </div>
      )}

      {/* Content */}
      <div className="card" style={{ padding: '28px' }}>
        {/* PROFILE TAB */}
        {activeTab === 'profile' && (
          <div className="animate-fade-in">
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '8px' }}>
              Basic Information
            </h2>
            <p style={{ color: 'var(--gray-500)', marginBottom: '24px', fontSize: '14px' }}>
              {isGuardian ? 'Your personal information' : 'Your personal and athlete information'}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>First Name</label>
                  <input
                    type="text"
                    value={form.given_name}
                    onChange={(e) => setForm({ ...form, given_name: e.target.value })}
                    placeholder="John"
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={form.family_name}
                    onChange={(e) => setForm({ ...form, family_name: e.target.value })}
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Email</label>
                <input
                  type="email"
                  defaultValue={userProfile?.email}
                  disabled
                  style={{ background: 'var(--gray-50)', color: 'var(--gray-500)' }}
                />
              </div>

              {/* Athlete-specific fields - hidden for guardians */}
              {!isGuardian && (
                <>
                  <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>Position</label>
                      <select
                        value={form.position}
                        onChange={(e) => setForm({ ...form, position: e.target.value })}
                      >
                        <option value="">Select...</option>
                        {POSITION_OPTIONS.map((pos) => (
                          <option key={pos} value={pos}>{pos}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>Jersey #</label>
                      <input
                        type="number"
                        min="0"
                        max="99"
                        value={form.jersey_number}
                        onChange={(e) => setForm({ ...form, jersey_number: e.target.value })}
                        placeholder="23"
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>Dominant Hand</label>
                      <select
                        value={form.dominant_hand}
                        onChange={(e) => setForm({ ...form, dominant_hand: e.target.value })}
                      >
                        <option value="">Select...</option>
                        <option value="right">Right</option>
                        <option value="left">Left</option>
                        <option value="ambidextrous">Ambidextrous</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>Height</label>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <select
                          value={parseHeight(form.height).feet}
                          onChange={(e) => setForm({ ...form, height: formatHeight(e.target.value, parseHeight(form.height).inches) })}
                          style={{ flex: 1 }}
                        >
                          <option value="">ft</option>
                          {[3, 4, 5, 6, 7].map((ft) => (
                            <option key={ft} value={ft.toString()}>{ft}'</option>
                          ))}
                        </select>
                        <select
                          value={parseHeight(form.height).inches}
                          onChange={(e) => setForm({ ...form, height: formatHeight(parseHeight(form.height).feet, e.target.value) })}
                          style={{ flex: 1 }}
                        >
                          <option value="">in</option>
                          {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((inch) => (
                            <option key={inch} value={inch.toString()}>{inch}"</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>Weight</label>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                          type="number"
                          min="50"
                          max="400"
                          value={parseWeight(form.weight)}
                          onChange={(e) => setForm({ ...form, weight: formatWeight(e.target.value) })}
                          placeholder="185"
                          style={{ flex: 1 }}
                        />
                        <span style={{ color: 'var(--gray-500)', fontSize: '14px' }}>lbs</span>
                      </div>
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>Date of Birth</label>
                      <input
                        type="date"
                        value={form.date_of_birth}
                        onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
                      />
                    </div>
                  </div>
                </>
              )}

              <SaveButton saving={saving} onClick={handleSave} />

              {/* Restart Profile Setup option - athletes only */}
              {!isGuardian && (
                <button
                  onClick={handleStartOnboarding}
                  style={{
                    marginTop: '24px',
                    padding: '8px 0',
                    background: 'none',
                    border: 'none',
                    color: 'var(--gray-400)',
                    fontSize: '13px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'color 0.15s ease',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--accent)')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--gray-400)')}
                >
                  <Sparkles size={14} />
                  Restart profile setup wizard
                </button>
              )}
            </div>
          </div>
        )}

        {/* TRAINING TAB */}
        {activeTab === 'training' && (
          <div className="animate-fade-in">
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Brain size={20} style={{ color: 'var(--accent)' }} />
              Training Profile
            </h2>
            <p style={{ color: 'var(--gray-500)', marginBottom: '24px', fontSize: '14px' }}>
              Help the AI personalize your training programs
            </p>

            {/* Learning Style */}
            <SectionHeader icon={Brain} title="How You Learn Best" />
            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', marginBottom: '24px' }}>
              {[
                { value: 'visual', label: 'Visual', desc: 'I learn by watching' },
                { value: 'kinesthetic', label: 'Hands-on', desc: 'I learn by doing' },
                { value: 'auditory', label: 'Auditory', desc: 'I learn by listening' },
                { value: 'reading', label: 'Reading', desc: 'I learn by reading' },
              ].map((opt) => (
                <SelectCard
                  key={opt.value}
                  selected={form.learning_style === opt.value}
                  onClick={() => setForm({ ...form, learning_style: opt.value })}
                  label={opt.label}
                  desc={opt.desc}
                />
              ))}
            </div>

            {/* Motivation Type */}
            <SectionHeader icon={Target} title="What Motivates You" />
            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', marginBottom: '24px' }}>
              {[
                { value: 'competition', label: 'Competition', desc: 'I thrive on winning' },
                { value: 'improvement', label: 'Improvement', desc: 'I focus on growth' },
                { value: 'team', label: 'Team Success', desc: 'I play for my team' },
                { value: 'fun', label: 'Fun', desc: 'I play for enjoyment' },
              ].map((opt) => (
                <SelectCard
                  key={opt.value}
                  selected={form.motivation_type === opt.value}
                  onClick={() => setForm({ ...form, motivation_type: opt.value })}
                  label={opt.label}
                  desc={opt.desc}
                />
              ))}
            </div>

            {/* Feedback Style */}
            <SectionHeader icon={User} title="Preferred Feedback Style" />
            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', marginBottom: '24px' }}>
              {[
                { value: 'direct', label: 'Direct', desc: 'Tell it like it is' },
                { value: 'encouraging', label: 'Encouraging', desc: 'Focus on positives' },
                { value: 'detailed', label: 'Detailed', desc: 'In-depth analysis' },
              ].map((opt) => (
                <SelectCard
                  key={opt.value}
                  selected={form.preferred_feedback_style === opt.value}
                  onClick={() => setForm({ ...form, preferred_feedback_style: opt.value })}
                  label={opt.label}
                  desc={opt.desc}
                />
              ))}
            </div>

            {/* Training Preferences */}
            <SectionHeader icon={Clock} title="Training Preferences" />
            <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: '24px' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Preferred Time</label>
                <select
                  value={form.preferred_workout_time}
                  onChange={(e) => setForm({ ...form, preferred_workout_time: e.target.value })}
                >
                  <option value="">Select...</option>
                  <option value="morning">Morning</option>
                  <option value="afternoon">Afternoon</option>
                  <option value="evening">Evening</option>
                  <option value="flexible">Flexible</option>
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Workout Duration</label>
                <select
                  value={form.preferred_workout_duration}
                  onChange={(e) => setForm({ ...form, preferred_workout_duration: e.target.value })}
                >
                  <option value="">Select...</option>
                  <option value="short">Short (15-30 min)</option>
                  <option value="medium">Medium (30-60 min)</option>
                  <option value="long">Long (60+ min)</option>
                </select>
              </div>
            </div>

            {/* Training Environment */}
            <SectionHeader icon={MapPin} title="Training Environment" />
            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', marginBottom: '24px' }}>
              {[
                { value: 'home', label: 'Home' },
                { value: 'gym', label: 'Gym' },
                { value: 'outdoor', label: 'Outdoor' },
                { value: 'school', label: 'School' },
              ].map((opt) => (
                <SelectCard
                  key={opt.value}
                  selected={form.training_environment === opt.value}
                  onClick={() => setForm({ ...form, training_environment: opt.value })}
                  label={opt.label}
                  compact
                />
              ))}
            </div>

            {/* Equipment */}
            <SectionHeader icon={Dumbbell} title="Equipment Available" />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '24px' }}>
              {EQUIPMENT_OPTIONS.map((item) => (
                <ChipToggle
                  key={item}
                  label={item}
                  selected={form.equipment_available.includes(item)}
                  onClick={() => toggleArrayItem('equipment_available', item)}
                />
              ))}
            </div>

            <SaveButton saving={saving} onClick={handleSave} />
          </div>
        )}

        {/* GOALS TAB */}
        {activeTab === 'goals' && (
          <div className="animate-fade-in">
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Target size={20} style={{ color: 'var(--accent)' }} />
              Goals & Aspirations
            </h2>
            <p style={{ color: 'var(--gray-500)', marginBottom: '24px', fontSize: '14px' }}>
              Define your athletic goals to help AI create targeted programs
            </p>

            {/* Athletic Goals */}
            <SectionHeader icon={Target} title="My Athletic Goals" />
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <input
                  type="text"
                  value={newGoal}
                  onChange={(e) => setNewGoal(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addGoal()}
                  placeholder="e.g., Improve 3-point shooting"
                  style={{ flex: 1 }}
                />
                <button onClick={addGoal} disabled={!newGoal.trim()} style={{ padding: '8px 16px' }}>
                  <Plus size={18} />
                </button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {form.athletic_goals.map((goal) => (
                  <div
                    key={goal}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      background: 'var(--accent-bg-medium)',
                      borderRadius: '20px',
                      fontSize: '13px',
                      color: 'var(--accent)',
                    }}
                  >
                    {goal}
                    <button
                      onClick={() => removeGoal(goal)}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '2px',
                        cursor: 'pointer',
                        color: 'var(--accent)',
                        display: 'flex',
                      }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
                {form.athletic_goals.length === 0 && (
                  <p style={{ color: 'var(--gray-400)', fontSize: '13px', fontStyle: 'italic' }}>
                    No goals added yet. Add some to help personalize your training!
                  </p>
                )}
              </div>
            </div>

            {/* Target Level */}
            <SectionHeader icon={GraduationCap} title="Target Level" />
            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', marginBottom: '24px' }}>
              {[
                { value: 'recreational', label: 'Recreational', desc: 'For fun & fitness' },
                { value: 'high_school', label: 'High School', desc: 'Compete at HS level' },
                { value: 'college', label: 'College', desc: 'Play college ball' },
                { value: 'professional', label: 'Professional', desc: 'Go pro' },
              ].map((opt) => (
                <SelectCard
                  key={opt.value}
                  selected={form.target_level === opt.value}
                  onClick={() => setForm({ ...form, target_level: opt.value })}
                  label={opt.label}
                  desc={opt.desc}
                />
              ))}
            </div>

            {/* Graduation Year */}
            <div className="form-group" style={{ marginBottom: '24px', maxWidth: '200px' }}>
              <label>Graduation Year</label>
              <input
                type="number"
                value={form.graduation_year}
                onChange={(e) => setForm({ ...form, graduation_year: e.target.value })}
                placeholder="2026"
                min="2024"
                max="2035"
              />
              <p style={{ fontSize: '12px', color: 'var(--gray-400)', marginTop: '4px' }}>
                Helps with recruiting context
              </p>
            </div>

            <SaveButton saving={saving} onClick={handleSave} />
          </div>
        )}

        {/* NOTIFICATIONS TAB */}
        {activeTab === 'notifications' && (
          <div className="animate-fade-in">
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '8px' }}>
              Notification Preferences
            </h2>
            <p style={{ color: 'var(--gray-500)', marginBottom: '24px', fontSize: '14px' }}>
              Control how you receive updates
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <NotificationToggle
                label="New coach feedback"
                description="Get notified when a coach comments on your video"
                checked={form.notify_on_comment}
                onChange={(checked) => setForm({ ...form, notify_on_comment: checked })}
              />
              <NotificationToggle
                label="Program assignments"
                description="Get notified when you're assigned a new program"
                checked={form.notify_on_assignment}
                onChange={(checked) => setForm({ ...form, notify_on_assignment: checked })}
              />
              <NotificationToggle
                label="Coach feedback"
                description="Get notified when a coach provides feedback"
                checked={form.notify_on_feedback}
                onChange={(checked) => setForm({ ...form, notify_on_feedback: checked })}
              />
            </div>

            <SaveButton saving={saving} onClick={handleSave} style={{ marginTop: '24px' }} />
          </div>
        )}

        {/* PRIVACY TAB */}
        {activeTab === 'privacy' && (
          <div className="animate-fade-in">
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '8px' }}>
              Privacy Settings
            </h2>
            <p style={{ color: 'var(--gray-500)', marginBottom: '24px', fontSize: '14px' }}>
              Control your data and privacy
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div
                style={{
                  padding: '20px',
                  background: 'var(--gray-50)',
                  borderRadius: 'var(--radius-md)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '16px'
                }}
              >
                <div
                  className="icon-container icon-container-sm"
                  style={{ background: 'var(--accent-bg-medium)', flexShrink: 0 }}
                >
                  <Download size={18} style={{ color: 'var(--accent)' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontWeight: '600', color: 'var(--gray-900)', marginBottom: '4px', fontSize: '15px' }}>
                    Data Export
                  </h3>
                  <p style={{ fontSize: '13px', color: 'var(--gray-500)', marginBottom: '12px' }}>
                    Download a copy of all your data including videos and progress
                  </p>
                  <button
                    className="btn-secondary"
                    onClick={handleExportData}
                    disabled={exporting}
                    style={{ padding: '8px 16px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                    {exporting ? 'Requesting...' : 'Request Data Export'}
                  </button>
                </div>
              </div>

              <div
                style={{
                  padding: '20px',
                  background: 'var(--gray-50)',
                  borderRadius: 'var(--radius-md)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '16px'
                }}
              >
                <div
                  className="icon-container icon-container-sm"
                  style={{ background: 'var(--accent-bg-medium)', flexShrink: 0 }}
                >
                  <Eye size={18} style={{ color: 'var(--accent)' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontWeight: '600', color: 'var(--gray-900)', marginBottom: '4px', fontSize: '15px' }}>
                    Profile Visibility
                  </h3>
                  <p style={{ fontSize: '13px', color: 'var(--gray-500)', marginBottom: '12px' }}>
                    Who can see your profile and progress
                  </p>
                  <select style={{ padding: '8px 12px', fontSize: '13px', width: 'auto' }}>
                    <option>Team members only</option>
                    <option>Coaches only</option>
                    <option>Family and coaches</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* FAMILY TAB */}
        {activeTab === 'family' && (
          <div className="animate-fade-in">
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '8px' }}>
              Family Access
            </h2>
            <p style={{ color: 'var(--gray-500)', marginBottom: '24px', fontSize: '14px' }}>
              View your connected family members and guardians
            </p>

            {/* Info card */}
            <div
              style={{
                background: 'var(--accent-bg-light)',
                border: '1px solid var(--accent-border-light)',
                borderRadius: 'var(--radius-md)',
                padding: '16px',
                marginBottom: '24px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <Shield size={20} style={{ color: 'var(--accent)', flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <h3 style={{ fontWeight: '600', color: 'var(--gray-900)', marginBottom: '4px', fontSize: '14px' }}>
                    Family Access
                  </h3>
                  <p style={{ fontSize: '13px', color: 'var(--gray-600)' }}>
                    Family members can view your training progress, videos, and highlights.
                    {ageGroup === 'under13' && ' Your coach manages who can access your profile.'}
                    {(ageGroup === '13to17' || ageGroup === 'unknown') && ' Ask your coach to add or remove family members.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Age-based notice for under-13 */}
            {ageGroup === 'under13' && (
              <div
                style={{
                  background: 'rgba(59, 130, 246, 0.08)',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  borderRadius: 'var(--radius-md)',
                  padding: '16px',
                  marginBottom: '24px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <AlertCircle size={20} style={{ color: 'rgb(59, 130, 246)', flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <h3 style={{ fontWeight: '600', color: 'var(--gray-900)', marginBottom: '4px', fontSize: '14px' }}>
                      Protected Account
                    </h3>
                    <p style={{ fontSize: '13px', color: 'var(--gray-600)' }}>
                      Your family connections are managed by your coach and guardians to keep you safe.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Family links list */}
            {familyLoading ? (
              <div className="flex-center" style={{ padding: '40px 20px' }}>
                <Loader2 size={32} className="animate-spin" style={{ color: 'var(--accent)' }} />
              </div>
            ) : familyLinks.length === 0 ? (
              <div
                style={{
                  padding: '40px 20px',
                  textAlign: 'center',
                  background: 'var(--gray-50)',
                  borderRadius: 'var(--radius-md)',
                }}
              >
                <Users size={32} style={{ color: 'var(--gray-400)', marginBottom: '12px' }} />
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '8px' }}>
                  No family members linked
                </h3>
                <p style={{ color: 'var(--gray-500)', fontSize: '14px' }}>
                  Ask your coach to invite a parent or guardian to view your progress.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {familyLinks.filter(l => l.status === 'APPROVED').length > 0 && (
                  <>
                    <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '4px' }}>
                      Connected
                    </h3>
                    {familyLinks.filter(l => l.status === 'APPROVED').map((link) => (
                      <FamilyMemberCard
                        key={link.link_id}
                        link={link}
                        orgId={currentOrg?.org_id}
                        onUpdate={async () => {
                          if (currentOrg) {
                            const { links } = await listFamilyLinks(currentOrg.org_id);
                            setFamilyLinks(links || []);
                          }
                        }}
                        canManageNotifications={canManageNotifications}
                        canRemove={canRemoveLinks}
                      />
                    ))}
                  </>
                )}
                {familyLinks.filter(l => l.status === 'PENDING').length > 0 && (
                  <>
                    <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--gray-700)', marginTop: '12px', marginBottom: '4px' }}>
                      Pending
                    </h3>
                    {familyLinks.filter(l => l.status === 'PENDING').map((link) => (
                      <FamilyMemberCard
                        key={link.link_id}
                        link={link}
                        orgId={currentOrg?.org_id}
                        onUpdate={async () => {
                          if (currentOrg) {
                            const { links } = await listFamilyLinks(currentOrg.org_id);
                            setFamilyLinks(links || []);
                          }
                        }}
                        canManageNotifications={false}
                        canRemove={false}
                      />
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* BILLING TAB */}
        {activeTab === 'billing' && (
          <div className="animate-fade-in">
            {/* Guardian Billing View - Shows all orgs */}
            {isGuardian ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
                  <div>
                    <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <CreditCard size={20} style={{ color: 'var(--accent)' }} />
                      Coaching Subscriptions
                    </h2>
                    <p style={{ color: 'var(--gray-500)', fontSize: '14px' }}>
                      {billingOrgs.length > 1
                        ? 'Manage coaching subscriptions across all your teams'
                        : 'Manage coaching subscriptions for your athletes'}
                    </p>
                  </div>
                  <button
                    onClick={loadAllOrgsBillingData}
                    disabled={allOrgsBillingLoading}
                    style={{
                      background: 'var(--gray-100)',
                      border: 'none',
                      padding: '8px',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <RefreshCw size={16} className={allOrgsBillingLoading ? 'animate-spin' : ''} style={{ color: 'var(--gray-600)' }} />
                  </button>
                </div>

                {subscriptionError && (
                  <div style={{
                    padding: '12px 16px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--error)',
                    marginBottom: '16px',
                  }}>
                    {subscriptionError}
                  </div>
                )}

                {allOrgsBillingLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                    <Loader2 size={24} className="animate-spin" style={{ margin: '0 auto 12px', color: 'var(--accent)' }} />
                    <div style={{ color: 'var(--gray-500)' }}>Loading...</div>
                  </div>
                ) : allOrgsBillingData.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '32px', background: 'var(--gray-50)', borderRadius: 'var(--radius-md)' }}>
                    <Users size={36} style={{ margin: '0 auto 12px', color: 'var(--gray-400)' }} />
                    <div style={{ color: 'var(--gray-500)' }}>No teams found.</div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {allOrgsBillingData.map(({ org, subscription: orgSub, plans: orgPlans, familyLinks: orgLinks }) => (
                      <div
                        key={org.org_id}
                        style={{
                          background: 'var(--gray-50)',
                          borderRadius: 'var(--radius-md)',
                          border: '1px solid var(--gray-200)',
                          overflow: 'hidden',
                        }}
                      >
                        {/* Org header - only show if multiple billing orgs */}
                        {billingOrgs.length > 1 && (
                          <div style={{
                            padding: '12px 16px',
                            background: 'var(--gray-100)',
                            borderBottom: '1px solid var(--gray-200)',
                            fontWeight: '600',
                            fontSize: '14px',
                            color: 'var(--gray-700)',
                          }}>
                            {org.name}
                          </div>
                        )}

                        <div style={{ padding: '16px' }}>
                          {/* Athletes in this org */}
                          {orgLinks.length > 0 && (
                            <div style={{ marginBottom: '16px' }}>
                              <div style={{ fontSize: '12px', fontWeight: '500', color: 'var(--gray-500)', marginBottom: '8px' }}>
                                Athletes
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {orgLinks.map(link => (
                                  <span
                                    key={link.link_id}
                                    style={{
                                      padding: '4px 10px',
                                      background: 'white',
                                      border: '1px solid var(--gray-200)',
                                      borderRadius: '16px',
                                      fontSize: '12px',
                                      color: 'var(--gray-700)',
                                    }}
                                  >
                                    {link.athlete_name || 'Athlete'}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Active subscription */}
                          {orgSub && orgSub.status === 'ACTIVE' ? (
                            <div style={{ padding: '12px', background: '#f0fdf4', borderRadius: 'var(--radius-sm)', border: '1px solid #bbf7d0' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div>
                                  <div style={{ fontWeight: '600', color: '#166534', marginBottom: '4px', fontSize: '14px' }}>
                                    Active Subscription
                                  </div>
                                  <div style={{ fontSize: '13px', color: '#15803d' }}>
                                    {orgPlans.find(p => p.plan_id === (orgSub as GuardianSubType).plan_id)?.name || 'Coaching Plan'}
                                    {orgSub.cancel_at_period_end && ' • Cancels at period end'}
                                  </div>
                                  {orgSub.current_period_end && (
                                    <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '6px' }}>
                                      Next billing: {new Date(orgSub.current_period_end).toLocaleDateString()}
                                    </div>
                                  )}
                                </div>
                                {!orgSub.cancel_at_period_end && (
                                  <button
                                    onClick={async () => {
                                      setCancellingGuardianSub(true);
                                      try {
                                        await cancelGuardianSubscription(org.org_id);
                                        await loadAllOrgsBillingData();
                                      } catch (err) {
                                        setSubscriptionError(err instanceof Error ? err.message : 'Failed to cancel');
                                      } finally {
                                        setCancellingGuardianSub(false);
                                      }
                                    }}
                                    disabled={cancellingGuardianSub}
                                    className="btn-secondary"
                                    style={{ padding: '4px 10px', fontSize: '12px' }}
                                  >
                                    {cancellingGuardianSub ? <Loader2 size={12} className="animate-spin" /> : 'Cancel'}
                                  </button>
                                )}
                              </div>
                            </div>
                          ) : orgLinks.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '16px', color: 'var(--gray-500)', fontSize: '14px' }}>
                              No linked athletes in this team yet.
                            </div>
                          ) : orgPlans.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '16px', color: 'var(--gray-500)', fontSize: '14px' }}>
                              No coaching plans available yet.
                            </div>
                          ) : (
                            <div style={{ display: 'grid', gap: '10px' }}>
                              {orgPlans.map(plan => (
                                <div
                                  key={plan.plan_id}
                                  style={{
                                    padding: '12px',
                                    background: 'white',
                                    borderRadius: 'var(--radius-sm)',
                                    border: '1px solid var(--gray-200)',
                                  }}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                                    <div style={{ flex: 1 }}>
                                      <div style={{ fontWeight: '600', color: 'var(--gray-900)', fontSize: '14px' }}>
                                        {plan.name}
                                      </div>
                                      {plan.description && (
                                        <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '2px' }}>
                                          {plan.description}
                                        </div>
                                      )}
                                    </div>
                                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                      <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--accent)' }}>
                                        ${(plan.amount_cents / 100).toFixed(2)}
                                      </div>
                                      <div style={{ fontSize: '11px', color: 'var(--gray-500)' }}>
                                        /{plan.interval === 'ONE_TIME' ? 'once' : plan.interval.toLowerCase().replace('ly', '')}
                                      </div>
                                    </div>
                                    <button
                                      onClick={async () => {
                                        setGuardianCheckoutLoading(plan.plan_id);
                                        try {
                                          const athleteIds = orgLinks.map(link => link.athlete_id);
                                          const { checkout_url } = await guardianSubscribe(org.org_id, {
                                            plan_id: plan.plan_id,
                                            athlete_ids: athleteIds,
                                            success_url: `${window.location.origin}/settings?billing=success`,
                                            cancel_url: `${window.location.origin}/settings?billing=cancelled`,
                                          });
                                          window.location.href = checkout_url;
                                        } catch (err) {
                                          setSubscriptionError(err instanceof Error ? err.message : 'Failed to start checkout');
                                          setGuardianCheckoutLoading(null);
                                        }
                                      }}
                                      disabled={guardianCheckoutLoading !== null}
                                      style={{
                                        padding: '8px 16px',
                                        background: 'var(--accent)',
                                        border: 'none',
                                        borderRadius: 'var(--radius-sm)',
                                        color: 'white',
                                        fontWeight: '500',
                                        fontSize: '13px',
                                        cursor: 'pointer',
                                        flexShrink: 0,
                                      }}
                                    >
                                      {guardianCheckoutLoading === plan.plan_id ? (
                                        <Loader2 size={14} className="animate-spin" />
                                      ) : (
                                        'Subscribe'
                                      )}
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              /* Athlete Billing View */
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
                  <div>
                    <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <CreditCard size={20} style={{ color: 'var(--accent)' }} />
                      My Subscription
                    </h2>
                    <p style={{ color: 'var(--gray-500)', fontSize: '14px' }}>
                      Manage your subscription and AI credits
                    </p>
                  </div>
                  <button
                    onClick={loadSubscription}
                    disabled={subscriptionLoading}
                    style={{
                      background: 'var(--gray-100)',
                      border: 'none',
                      padding: '8px',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <RefreshCw size={16} className={subscriptionLoading ? 'animate-spin' : ''} style={{ color: 'var(--gray-600)' }} />
                  </button>
                </div>

                {subscriptionError && (
                  <div style={{
                    padding: '12px 16px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--error)',
                    marginBottom: '16px',
                  }}>
                    {subscriptionError}
                  </div>
                )}

                {/* Coach Subscriptions Section - Shows all orgs */}
                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Users size={18} style={{ color: 'var(--accent)' }} />
                    Coach Subscriptions
                  </h3>
                  <p style={{ color: 'var(--gray-500)', fontSize: '14px', marginBottom: '16px' }}>
                    {billingOrgs.length > 1
                      ? 'Manage subscriptions across all your teams and organizations'
                      : 'Subscribe directly to coaching services from your organization'}
                  </p>

                  {allOrgsBillingLoading ? (
                    <div style={{ textAlign: 'center', padding: '24px' }}>
                      <Loader2 size={20} className="animate-spin" style={{ color: 'var(--accent)' }} />
                    </div>
                  ) : allOrgsBillingData.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '24px', background: 'var(--gray-50)', borderRadius: 'var(--radius-md)' }}>
                      <Users size={32} style={{ margin: '0 auto 12px', color: 'var(--gray-400)' }} />
                      <div style={{ color: 'var(--gray-500)', fontSize: '14px' }}>
                        No organizations found.
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      {allOrgsBillingData.map(({ org, subscription: orgSub, plans: orgPlans }) => (
                        <div
                          key={org.org_id}
                          style={{
                            background: 'var(--gray-50)',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid var(--gray-200)',
                            overflow: 'hidden',
                          }}
                        >
                          {/* Org header - only show if multiple billing orgs */}
                          {billingOrgs.length > 1 && (
                            <div style={{
                              padding: '12px 16px',
                              background: 'var(--gray-100)',
                              borderBottom: '1px solid var(--gray-200)',
                              fontWeight: '600',
                              fontSize: '14px',
                              color: 'var(--gray-700)',
                            }}>
                              {org.name}
                            </div>
                          )}

                          <div style={{ padding: '16px' }}>
                            {/* Active subscription */}
                            {orgSub && orgSub.status === 'ACTIVE' ? (
                              <div style={{ padding: '12px', background: '#f0fdf4', borderRadius: 'var(--radius-sm)', border: '1px solid #bbf7d0' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                  <div>
                                    <div style={{ fontWeight: '600', color: '#166534', marginBottom: '4px', fontSize: '14px' }}>
                                      Active Subscription
                                    </div>
                                    <div style={{ fontSize: '13px', color: '#15803d' }}>
                                      {orgPlans.find(p => p.plan_id === (orgSub as AthleteCoachSubType).payment_plan_id)?.name || 'Coaching Plan'}
                                      {orgSub.cancel_at_period_end && ' • Cancels at period end'}
                                    </div>
                                    {orgSub.current_period_end && (
                                      <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '6px' }}>
                                        Next billing: {new Date(orgSub.current_period_end).toLocaleDateString()}
                                      </div>
                                    )}
                                  </div>
                                  {!orgSub.cancel_at_period_end && (
                                    <button
                                      onClick={async () => {
                                        setCancellingAthleteSub(true);
                                        try {
                                          await cancelAthleteDirectSubscription(org.org_id);
                                          await loadAllOrgsBillingData();
                                        } catch (err) {
                                          setSubscriptionError(err instanceof Error ? err.message : 'Failed to cancel');
                                        } finally {
                                          setCancellingAthleteSub(false);
                                        }
                                      }}
                                      disabled={cancellingAthleteSub}
                                      className="btn-secondary"
                                      style={{ padding: '4px 10px', fontSize: '12px' }}
                                    >
                                      {cancellingAthleteSub ? <Loader2 size={12} className="animate-spin" /> : 'Cancel'}
                                    </button>
                                  )}
                                </div>
                              </div>
                            ) : orgPlans.length === 0 ? (
                              <div style={{ textAlign: 'center', padding: '16px', color: 'var(--gray-500)', fontSize: '14px' }}>
                                No coaching plans available yet.
                              </div>
                            ) : (
                              <div style={{ display: 'grid', gap: '10px' }}>
                                {orgPlans.map(plan => (
                                  <div
                                    key={plan.plan_id}
                                    style={{
                                      padding: '12px',
                                      background: 'white',
                                      borderRadius: 'var(--radius-sm)',
                                      border: '1px solid var(--gray-200)',
                                    }}
                                  >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                                      <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: '600', color: 'var(--gray-900)', fontSize: '14px' }}>
                                          {plan.name}
                                        </div>
                                        {plan.description && (
                                          <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '2px' }}>
                                            {plan.description}
                                          </div>
                                        )}
                                      </div>
                                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                        <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--accent)' }}>
                                          ${(plan.amount_cents / 100).toFixed(2)}
                                        </div>
                                        <div style={{ fontSize: '11px', color: 'var(--gray-500)' }}>
                                          /{plan.interval === 'ONE_TIME' ? 'once' : plan.interval.toLowerCase().replace('ly', '')}
                                        </div>
                                      </div>
                                      <button
                                        onClick={async () => {
                                          setAthleteCheckoutLoading(plan.plan_id);
                                          try {
                                            const { checkout_url } = await athleteSubscribe(org.org_id, {
                                              plan_id: plan.plan_id,
                                              success_url: `${window.location.origin}/settings?billing=success`,
                                              cancel_url: `${window.location.origin}/settings?billing=cancelled`,
                                            });
                                            window.location.href = checkout_url;
                                          } catch (err) {
                                            setSubscriptionError(err instanceof Error ? err.message : 'Failed to start checkout');
                                            setAthleteCheckoutLoading(null);
                                          }
                                        }}
                                        disabled={athleteCheckoutLoading !== null}
                                        style={{
                                          padding: '8px 16px',
                                          background: 'var(--accent)',
                                          border: 'none',
                                          borderRadius: 'var(--radius-sm)',
                                          color: 'white',
                                          fontWeight: '500',
                                          fontSize: '13px',
                                          cursor: 'pointer',
                                          flexShrink: 0,
                                        }}
                                      >
                                        {athleteCheckoutLoading === plan.plan_id ? (
                                          <Loader2 size={14} className="animate-spin" />
                                        ) : (
                                          'Subscribe'
                                        )}
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* My Subscription - Platform subscription section */}
                {/* Hide coach tiers - users who are coaches manage billing in coach portal */}
                {subscription && (subscription.tier === 'COACH_BASIC' || subscription.tier === 'COACH_PRO' || subscription.tier === 'ENTERPRISE') ? (
                  <div style={{
                    padding: '16px 20px',
                    background: 'var(--gray-50)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--gray-200)',
                    marginBottom: '24px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <CreditCard size={20} style={{ color: 'var(--accent)' }} />
                      <div>
                        <div style={{ fontWeight: '600', color: 'var(--gray-900)', marginBottom: '2px' }}>
                          Platform Subscription
                        </div>
                        <div style={{ fontSize: '14px', color: 'var(--gray-500)' }}>
                          You have an active coach subscription. Manage billing in the Coach Portal.
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CreditCard size={18} style={{ color: 'var(--accent)' }} />
                    Platform Subscription
                  </h3>
                </div>

                {subscriptionLoading ? (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <Loader2 size={24} className="animate-spin" style={{ margin: '0 auto 12px', color: 'var(--accent)' }} />
                <div style={{ color: 'var(--gray-500)' }}>Loading subscription...</div>
              </div>
            ) : subscription ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* Subscription Status */}
                <div style={{ padding: '20px', background: 'var(--gray-50)', borderRadius: 'var(--radius-md)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--gray-900)' }}>
                        {formatTierName(subscription.tier)}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                        <span style={{
                          display: 'inline-block',
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: getStatusColor(subscription.status)
                        }} />
                        <span style={{ color: 'var(--gray-500)', fontSize: '14px' }}>
                          {subscription.status}
                        </span>
                        {subscription.cancel_at_period_end && (
                          <span style={{ color: '#f59e0b', fontSize: '13px' }}>
                            (Cancels at period end)
                          </span>
                        )}
                      </div>
                    </div>
                    {subscription.subscription_id && (
                      <button
                        onClick={openBillingPortal}
                        disabled={portalLoading}
                        style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '8px' }}
                      >
                        {portalLoading ? <Loader2 size={16} className="animate-spin" /> : <ExternalLink size={16} />}
                        Manage
                      </button>
                    )}
                  </div>
                </div>

                {/* AI Credits */}
                <div style={{ padding: '20px', background: 'linear-gradient(135deg, var(--accent-bg-medium) 0%, rgba(123, 75, 216, 0.08) 100%)', borderRadius: 'var(--radius-md)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <Coins size={20} style={{ color: 'var(--accent)' }} />
                    <span style={{ fontWeight: '600', color: 'var(--gray-900)' }}>AI Credits</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <div>
                      <div style={{ fontSize: '36px', fontWeight: '700', color: 'var(--accent)' }}>
                        {subscription.ai_credits_balance}
                      </div>
                      <div style={{ color: 'var(--gray-500)', fontSize: '14px' }}>
                        of {subscription.ai_credits_monthly_allocation} monthly credits
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      {subscription.credits_reset_at && (
                        <div style={{ color: 'var(--gray-500)', fontSize: '13px' }}>
                          Resets {new Date(subscription.credits_reset_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Credits progress bar */}
                  <div style={{ marginTop: '12px', background: 'var(--accent-bg-strong)', height: '8px', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{
                      background: 'var(--accent)',
                      height: '100%',
                      width: `${Math.min(100, (subscription.ai_credits_balance / Math.max(1, subscription.ai_credits_monthly_allocation)) * 100)}%`,
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>

                {/* Billing Period */}
                {subscription.current_period_end && (
                  <div style={{ color: 'var(--gray-500)', fontSize: '14px' }}>
                    Current period ends: {new Date(subscription.current_period_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </div>
                )}

                {/* Upgrade Options for FREE tier */}
                {subscription.tier === 'FREE' && (
                  <div style={{ marginTop: '24px', padding: '20px', background: 'linear-gradient(135deg, var(--accent-bg-subtle) 0%, var(--accent-bg-medium) 100%)', borderRadius: 'var(--radius-md)', border: '1px solid var(--accent-border-light)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                      <Sparkles size={18} style={{ color: 'var(--accent)' }} />
                      <span style={{ fontWeight: '600', color: 'var(--gray-900)' }}>Upgrade for More Features</span>
                    </div>
                    <div style={{ display: 'grid', gap: '12px' }}>
                      <button
                        onClick={() => startCheckout('ATHLETE_BASIC')}
                        disabled={checkoutLoading !== null}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          background: 'white',
                          border: '1px solid var(--gray-200)',
                          borderRadius: 'var(--radius-md)',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <div style={{ textAlign: 'left' }}>
                          <div style={{ fontWeight: '600', color: 'var(--gray-900)' }}>
                            {pricing?.tiers.ATHLETE_BASIC.name || 'Athlete Basic'}
                          </div>
                          <div style={{ fontSize: '13px', color: 'var(--gray-500)' }}>
                            {pricing?.tiers.ATHLETE_BASIC.ai_credits_monthly || 50} AI credits/month
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontWeight: '600', color: 'var(--accent)' }}>
                            {pricing?.tiers.ATHLETE_BASIC.price_display || '$4.99'}/mo
                          </span>
                          {checkoutLoading === 'ATHLETE_BASIC' ? <Loader2 size={16} className="animate-spin" /> : <ChevronRight size={16} />}
                        </div>
                      </button>
                      <button
                        onClick={() => startCheckout('ATHLETE_PRO')}
                        disabled={checkoutLoading !== null}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          background: 'linear-gradient(135deg, var(--accent) 0%, #6366f1 100%)',
                          border: 'none',
                          borderRadius: 'var(--radius-md)',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <div style={{ textAlign: 'left' }}>
                          <div style={{ fontWeight: '600', color: 'white' }}>
                            {pricing?.tiers.ATHLETE_PRO.name || 'Athlete Pro'}
                          </div>
                          <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.8)' }}>
                            {pricing?.tiers.ATHLETE_PRO.ai_credits_monthly || 200} AI credits/month + Priority
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'white' }}>
                          <span style={{ fontWeight: '600' }}>
                            {pricing?.tiers.ATHLETE_PRO.price_display || '$9.99'}/mo
                          </span>
                          {checkoutLoading === 'ATHLETE_PRO' ? <Loader2 size={16} className="animate-spin" /> : <ChevronRight size={16} />}
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* No subscription - show pricing options */
              <div style={{ padding: '20px 0' }}>
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    background: 'linear-gradient(135deg, var(--accent-bg-medium) 0%, rgba(123, 75, 216, 0.08) 100%)',
                    borderRadius: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px'
                  }}>
                    <Sparkles size={28} style={{ color: 'var(--accent)' }} />
                  </div>
                  <div style={{ fontWeight: '600', color: 'var(--gray-900)', marginBottom: '8px' }}>
                    Unlock AI-Powered Features
                  </div>
                  <div style={{ color: 'var(--gray-500)', fontSize: '14px' }}>
                    Get highlight reels, AI coaching insights, and more
                  </div>
                </div>

                <div style={{ display: 'grid', gap: '16px' }}>
                  {/* Athlete Basic */}
                  <div style={{
                    padding: '20px',
                    background: 'var(--gray-50)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--gray-200)',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <div>
                        <div style={{ fontWeight: '600', color: 'var(--gray-900)', fontSize: '18px' }}>
                          {pricing?.tiers.ATHLETE_BASIC.name || 'Athlete Basic'}
                        </div>
                        <div style={{ color: 'var(--gray-500)', fontSize: '14px' }}>
                          {pricing?.tiers.ATHLETE_BASIC.description || 'Great for getting started'}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--gray-900)' }}>
                          {pricing?.tiers.ATHLETE_BASIC.price_display || '$4.99'}
                        </div>
                        <div style={{ fontSize: '13px', color: 'var(--gray-500)' }}>/month</div>
                      </div>
                    </div>
                    <div style={{ fontSize: '14px', color: 'var(--gray-600)', marginBottom: '16px' }}>
                      {pricing?.tiers.ATHLETE_BASIC.features.map((feature, idx) => (
                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                          <Check size={16} style={{ color: '#10b981' }} />
                          <span>{feature}</span>
                        </div>
                      )) || (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                            <Check size={16} style={{ color: '#10b981' }} />
                            <span>50 AI credits per month</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                            <Check size={16} style={{ color: '#10b981' }} />
                            <span>AI highlight reels</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Check size={16} style={{ color: '#10b981' }} />
                            <span>Basic coaching insights</span>
                          </div>
                        </>
                      )}
                    </div>
                    <button
                      onClick={() => startCheckout('ATHLETE_BASIC')}
                      disabled={checkoutLoading !== null}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: 'white',
                        border: '1px solid var(--gray-300)',
                        borderRadius: 'var(--radius-md)',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                      }}
                    >
                      {checkoutLoading === 'ATHLETE_BASIC' ? <Loader2 size={16} className="animate-spin" /> : null}
                      Get Started
                    </button>
                  </div>

                  {/* Athlete Pro */}
                  <div style={{
                    padding: '20px',
                    background: 'linear-gradient(135deg, var(--accent-bg-medium) 0%, rgba(123, 75, 216, 0.08) 100%)',
                    borderRadius: 'var(--radius-md)',
                    border: '2px solid var(--accent)',
                    position: 'relative',
                  }}>
                    {pricing?.tiers.ATHLETE_PRO.popular !== false && (
                      <div style={{
                        position: 'absolute',
                        top: '-10px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: 'var(--accent)',
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                      }}>
                        POPULAR
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <div>
                        <div style={{ fontWeight: '600', color: 'var(--gray-900)', fontSize: '18px' }}>
                          {pricing?.tiers.ATHLETE_PRO.name || 'Athlete Pro'}
                        </div>
                        <div style={{ color: 'var(--gray-500)', fontSize: '14px' }}>
                          {pricing?.tiers.ATHLETE_PRO.description || 'For serious athletes'}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--accent)' }}>
                          {pricing?.tiers.ATHLETE_PRO.price_display || '$9.99'}
                        </div>
                        <div style={{ fontSize: '13px', color: 'var(--gray-500)' }}>/month</div>
                      </div>
                    </div>
                    <div style={{ fontSize: '14px', color: 'var(--gray-600)', marginBottom: '16px' }}>
                      {pricing?.tiers.ATHLETE_PRO.features.map((feature, idx) => (
                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                          <Check size={16} style={{ color: '#10b981' }} />
                          <span>{feature}</span>
                        </div>
                      )) || (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                            <Check size={16} style={{ color: '#10b981' }} />
                            <span><strong>200</strong> AI credits per month</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                            <Check size={16} style={{ color: '#10b981' }} />
                            <span>Unlimited highlight reels</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                            <Check size={16} style={{ color: '#10b981' }} />
                            <span>Advanced coaching insights</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Check size={16} style={{ color: '#10b981' }} />
                            <span>Priority support</span>
                          </div>
                        </>
                      )}
                    </div>
                    <button
                      onClick={() => startCheckout('ATHLETE_PRO')}
                      disabled={checkoutLoading !== null}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: 'linear-gradient(135deg, var(--accent) 0%, #6366f1 100%)',
                        border: 'none',
                        borderRadius: 'var(--radius-md)',
                        fontWeight: '600',
                        color: 'white',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                      }}
                    >
                      {checkoutLoading === 'ATHLETE_PRO' ? <Loader2 size={16} className="animate-spin" /> : null}
                      Upgrade to Pro
                    </button>
                  </div>
                </div>
              </div>
            )}
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* ORGANIZATION TAB */}
        {activeTab === 'organization' && (
          <div className="animate-fade-in">
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '8px' }}>
              Organization Settings
            </h2>
            <p style={{ color: 'var(--gray-500)', marginBottom: '24px', fontSize: '14px' }}>
              Switch between your organizations
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {allOrgs.map((org) => {
                const isActive = currentOrg?.org_id === org.org_id;
                return (
                  <div
                    key={org.org_id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '16px 20px',
                      borderRadius: 'var(--radius-md)',
                      background: isActive ? 'var(--accent-bg-light)' : 'var(--gray-50)',
                      border: isActive ? '2px solid var(--accent)' : '1px solid var(--gray-200)',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        background: isActive ? 'var(--accent)' : 'var(--gray-200)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <Building2 size={24} style={{ color: isActive ? 'white' : 'var(--gray-500)' }} />
                      </div>
                      <div>
                        <div style={{ fontWeight: '600', color: 'var(--gray-900)', fontSize: '16px' }}>
                          {org.name}
                        </div>
                        <div style={{
                          fontSize: '14px',
                          color: isActive ? 'var(--accent)' : 'var(--gray-500)',
                          textTransform: 'capitalize',
                          marginTop: '2px'
                        }}>
                          {org.role.toLowerCase()}
                        </div>
                      </div>
                    </div>
                    {!isActive ? (
                      <button
                        onClick={() => {
                          setCurrentOrg(org);
                          setFamilyLinks([]);
                        }}
                        className="btn-secondary"
                        style={{ padding: '10px 20px', fontSize: '14px' }}
                      >
                        Switch
                      </button>
                    ) : (
                      <span style={{
                        fontSize: '14px',
                        color: 'var(--accent)',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: 'var(--accent-bg-medium)',
                        padding: '8px 16px',
                        borderRadius: 'var(--radius-md)',
                      }}>
                        <Check size={16} />
                        Active
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Family Member Card Component
function FamilyMemberCard({
  link,
  orgId,
  onUpdate,
  canManageNotifications,
  canRemove,
}: {
  link: FamilyLink;
  orgId?: string;
  onUpdate: () => void;
  canManageNotifications: boolean;
  canRemove: boolean;
}) {
  const [showSettings, setShowSettings] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [notifyWeekly, setNotifyWeekly] = useState(link.notify_weekly_progress ?? true);
  const [notifyMonthly, setNotifyMonthly] = useState(link.notify_monthly_progress ?? true);
  const [notifyFlags, setNotifyFlags] = useState(link.notify_flags ?? true);
  const isApproved = link.status === 'APPROVED';

  async function handleToggleNotification(type: 'weekly' | 'monthly' | 'flags', value: boolean) {
    if (!orgId) return;
    setSaving(true);
    try {
      const preferences: Record<string, boolean> = {};
      if (type === 'weekly') {
        preferences.notify_weekly_progress = value;
        setNotifyWeekly(value);
      } else if (type === 'monthly') {
        preferences.notify_monthly_progress = value;
        setNotifyMonthly(value);
      } else {
        preferences.notify_flags = value;
        setNotifyFlags(value);
      }
      await updateNotificationPreferences(orgId, link.link_id, preferences);
    } catch (err) {
      console.error('Failed to update notification preferences:', err);
      if (type === 'weekly') setNotifyWeekly(!value);
      else if (type === 'monthly') setNotifyMonthly(!value);
      else setNotifyFlags(!value);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!orgId) return;
    setDeleting(true);
    try {
      await deleteFamilyLink(orgId, link.link_id);
      onUpdate();
    } catch (err) {
      console.error('Failed to delete family link:', err);
      alert('Failed to remove family member. Please try again.');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  }

  return (
    <div
      style={{
        padding: '16px',
        background: 'var(--gray-50)',
        borderRadius: 'var(--radius-md)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: isApproved ? 'rgba(16, 185, 129, 0.1)' : 'var(--gray-100)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Users size={20} style={{ color: isApproved ? 'rgb(16, 185, 129)' : 'var(--gray-400)' }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <h4 style={{ fontWeight: '600', color: 'var(--gray-900)', fontSize: '14px' }}>
              {link.guardian_name || link.guardian_email || 'Family Member'}
            </h4>
            {isApproved && (
              <CheckCircle size={14} style={{ color: 'rgb(16, 185, 129)', flexShrink: 0 }} />
            )}
          </div>
          <p style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '2px' }}>
            <span style={{ textTransform: 'capitalize' }}>{link.relationship.toLowerCase()}</span>
            <span style={{ margin: '0 6px' }}>•</span>
            <span>{isApproved ? 'Connected' : 'Pending'}</span>
          </p>
        </div>

        {isApproved && (canManageNotifications || canRemove) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {canManageNotifications && (
              <button
                onClick={() => setShowSettings(!showSettings)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '8px',
                  cursor: 'pointer',
                  color: showSettings ? 'var(--accent)' : 'var(--gray-500)',
                  display: 'flex',
                  boxShadow: 'none',
                }}
                title="Notification settings"
              >
                {(notifyWeekly || notifyMonthly || notifyFlags) ? (
                  <Bell size={18} />
                ) : (
                  <BellOff size={18} />
                )}
              </button>
            )}
            {canRemove && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '8px',
                  cursor: 'pointer',
                  color: 'var(--gray-500)',
                  display: 'flex',
                  boxShadow: 'none',
                }}
                title="Remove family member"
              >
                <Trash2 size={18} />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Notification Settings Panel */}
      {showSettings && isApproved && canManageNotifications && (
        <div
          style={{
            marginTop: '16px',
            paddingTop: '16px',
            borderTop: '1px solid var(--gray-200)',
          }}
        >
          <h5 style={{ fontSize: '13px', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '12px' }}>
            Email Notifications
          </h5>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <FamilyNotificationToggle
              label="Weekly Progress"
              checked={notifyWeekly}
              onChange={(v) => handleToggleNotification('weekly', v)}
              disabled={saving}
            />
            <FamilyNotificationToggle
              label="Monthly Summaries"
              checked={notifyMonthly}
              onChange={(v) => handleToggleNotification('monthly', v)}
              disabled={saving}
            />
            <FamilyNotificationToggle
              label="Coach Feedback"
              checked={notifyFlags}
              onChange={(v) => handleToggleNotification('flags', v)}
              disabled={saving}
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div
          style={{
            marginTop: '16px',
            paddingTop: '16px',
            borderTop: '1px solid var(--gray-200)',
          }}
        >
          <p style={{ fontSize: '13px', color: 'var(--gray-700)', marginBottom: '12px' }}>
            Remove <strong>{link.guardian_name || 'this family member'}</strong>?
          </p>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setShowDeleteConfirm(false)}
              disabled={deleting}
              style={{
                flex: 1,
                padding: '8px 12px',
                fontSize: '13px',
                background: 'white',
                border: '1px solid var(--gray-200)',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              style={{
                flex: 1,
                padding: '8px 12px',
                fontSize: '13px',
                background: 'var(--error)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
              }}
            >
              {deleting ? <Loader2 size={14} className="animate-spin" /> : null}
              {deleting ? 'Removing...' : 'Remove'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function FamilyNotificationToggle({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <span style={{ fontSize: '13px', color: 'var(--gray-700)' }}>{label}</span>
      <button
        onClick={() => !disabled && onChange(!checked)}
        style={{
          width: '36px',
          height: '20px',
          borderRadius: '10px',
          background: checked ? 'var(--accent)' : 'var(--gray-300)',
          border: 'none',
          cursor: disabled ? 'not-allowed' : 'pointer',
          position: 'relative',
          transition: 'background 0.2s',
          padding: 0,
          boxShadow: 'none',
        }}
      >
        <span
          style={{
            display: 'block',
            width: '16px',
            height: '16px',
            background: 'white',
            borderRadius: '50%',
            position: 'absolute',
            top: '2px',
            left: checked ? '18px' : '2px',
            transition: 'left 0.2s',
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
          }}
        />
      </button>
    </label>
  );
}

// Helper Components

function SectionHeader({ icon: Icon, title }: { icon: typeof Brain; title: string }) {
  return (
    <h3 style={{
      fontSize: '14px',
      fontWeight: '600',
      color: 'var(--gray-700)',
      marginBottom: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    }}>
      <Icon size={16} style={{ color: 'var(--gray-400)' }} />
      {title}
    </h3>
  );
}

function SelectCard({
  selected,
  onClick,
  label,
  desc,
  compact,
}: {
  selected: boolean;
  onClick: () => void;
  label: string;
  desc?: string;
  compact?: boolean;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: compact ? '12px' : '16px',
        background: selected ? 'var(--accent-bg-medium)' : 'var(--gray-50)',
        border: `2px solid ${selected ? 'var(--accent)' : 'transparent'}`,
        borderRadius: 'var(--radius-md)',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        textAlign: 'center',
      }}
    >
      <p style={{ fontWeight: '600', color: selected ? 'var(--accent)' : 'var(--gray-900)', fontSize: '14px' }}>
        {label}
      </p>
      {desc && (
        <p style={{ fontSize: '11px', color: 'var(--gray-500)', marginTop: '4px' }}>
          {desc}
        </p>
      )}
    </div>
  );
}

function ChipToggle({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '8px 14px',
        borderRadius: '20px',
        border: `1px solid ${selected ? 'var(--accent)' : 'var(--gray-200)'}`,
        background: selected ? 'var(--accent-bg-medium)' : 'white',
        color: selected ? 'var(--accent)' : 'var(--gray-600)',
        fontSize: '13px',
        fontWeight: selected ? '600' : '400',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      }}
    >
      {label}
    </button>
  );
}

function ToggleSwitch({ checked, onChange }: { checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      style={{
        width: '44px',
        height: '24px',
        borderRadius: '12px',
        background: checked ? 'var(--accent)' : 'var(--gray-300)',
        border: 'none',
        cursor: 'pointer',
        position: 'relative',
        transition: 'background 0.2s ease',
        padding: 0,
        boxShadow: 'none',
        flexShrink: 0,
      }}
    >
      <span
        style={{
          display: 'block',
          width: '18px',
          height: '18px',
          background: 'white',
          borderRadius: '50%',
          position: 'absolute',
          top: '3px',
          left: checked ? '23px' : '3px',
          transition: 'left 0.2s ease',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }}
      />
    </button>
  );
}

function NotificationToggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div
      className="flex-between"
      style={{
        padding: '16px',
        background: 'var(--gray-50)',
        borderRadius: 'var(--radius-md)',
        gap: '16px',
      }}
    >
      <div>
        <h3 style={{ fontWeight: '500', color: 'var(--gray-900)', fontSize: '14px' }}>{label}</h3>
        <p style={{ fontSize: '13px', color: 'var(--gray-500)', marginTop: '2px' }}>{description}</p>
      </div>
      <ToggleSwitch checked={checked} onChange={onChange} />
    </div>
  );
}

function SaveButton({ saving, onClick, style }: { saving: boolean; onClick: () => void; style?: React.CSSProperties }) {
  return (
    <button
      onClick={onClick}
      disabled={saving}
      style={{ alignSelf: 'flex-start', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px', ...style }}
    >
      {saving ? <Loader2 size={18} className="animate-spin" /> : null}
      {saving ? 'Saving...' : 'Save Changes'}
    </button>
  );
}
